import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';

interface UseListPanelStateConfig<TFilters> {
  emptyFilters: TFilters;
  countFilters?: (filters: TFilters) => number;
  debounceMs?: number;
  pageSize?: number;
  resetKey?: unknown;
}

export function useListPanelState<TFilters>({
  emptyFilters,
  countFilters,
  debounceMs = 300,
  pageSize = 20,
  resetKey,
}: UseListPanelStateConfig<TFilters>) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TFilters>(emptyFilters);
  const debouncedFilters = useDebounce(filters, debounceMs);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, resetKey]);

  const activeFilterCount = useMemo(() => countFilters?.(filters) ?? 0, [countFilters, filters]);

  function setFilter<K extends keyof TFilters>(key: K, value: TFilters[K]) {
    setFilters((previous) => ({ ...previous, [key]: value }));
  }

  function updateFilters(patch: Partial<TFilters>) {
    setFilters((previous) => ({ ...previous, ...patch }));
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setPage(1);
  }

  function getTotalPages(total: number) {
    return Math.max(1, Math.ceil(total / pageSize));
  }

  return {
    page,
    setPage,
    filters,
    setFilter,
    updateFilters,
    resetFilters,
    debouncedFilters,
    activeFilterCount,
    getTotalPages,
  };
}

interface UseListPanelSearchConfig {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

export function useListPanelSearch({
  value,
  onChange,
  debounceMs = 300,
}: UseListPanelSearchConfig) {
  const [searchInput, setSearchInputState] = useState(value);
  const searchInputRef = useRef(searchInput);
  const syncingExternalValueRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debouncedSearch = useDebounce(searchInput, debounceMs);

  const setSearchInput = useCallback((nextValue: string) => {
    syncingExternalValueRef.current = false;
    searchInputRef.current = nextValue;
    setSearchInputState(nextValue);
  }, []);

  useEffect(() => {
    if (value === searchInputRef.current) return;
    syncingExternalValueRef.current = true;
    searchInputRef.current = value;
    setSearchInputState(value);
  }, [value]);

  useEffect(() => {
    if (syncingExternalValueRef.current) {
      if (debouncedSearch === value) {
        syncingExternalValueRef.current = false;
      }
      return;
    }
    if (debouncedSearch !== value) {
      onChangeRef.current(debouncedSearch);
    }
  }, [debouncedSearch, value]);

  return { searchInput, setSearchInput, debouncedSearch };
}
